import React, { Component } from "react";
import { connect } from "react-redux";
import { defaultState } from "../reducers/ManagementContractData";
import { dispatchX } from "@my-dld/utils/StoreUtils";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { IStore } from "@my-dld/reducers/store";
import DefaultLoader, { EjariCtxContractLoader } from "../loaders/ContractLoader";
import { RememberThis } from "@eres/forms/decoratetors";
import { ManagementContractDataAction, ManagementContractDataState } from "../reducers/ManagementContractData";
import EjariApi from "@eres/ejari-api";
import {
	GetManagementContractDetails,
	MCContractCreateRequest,
	MCTerminateRequest,
	OptionEJLookup,
	Responsibilities
} from "@eres/ejari-api/Entity/management_contract";
import { TenantDocumentLookup } from "@eres/ejari-api/Entity";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import Strings from "@my-dld/assets/strings";
import { getEjariActiveUserId, getEjariUserType } from "@10x/sso";
import { TenancyContactAction } from "../constants";
import { trackEvent } from "@my-dld/utils/Analytics";

export interface IManagementContractContext extends ManagementContractDataState {
	terminateManagementContract: ( ctx: IManagementContractContext, request: MCTerminateRequest, documents: TenantDocumentLookup[] ) => Promise<any>;
	submitManagementContract: ( ctx: IManagementContractContext ) => Promise<any>;
}

interface Props extends IManagementContractContext {
	loader?: EjariCtxContractLoader;
	onContractActionFinished: ( ctx: IManagementContractContext ) => void;
}

export const defaultCtx: IManagementContractContext = {
	...defaultState,
	terminateManagementContract: () => Promise.reject( "Not ready" ),
	submitManagementContract: () => Promise.reject( "Not ready" ),
};

export const ManagementContractContext = React.createContext( defaultCtx );

interface State {
	tempContractId?: string;
	documentTempId?: number;
	managementContract?: GetManagementContractDetails;
}

class ManagementContractContextWrapper extends Component<Props, State> {
	static defaultProps: Partial<Props> = {
		loader: DefaultLoader,
	};

	constructor( props: Props ) {
		super( props );
		this.state = {};
	}

	@RememberThis()
	public static buildContext( cmp: ManagementContractContextWrapper, props: Props ) {
		return {
			...props,
		} as IManagementContractContext;
	}

	componentDidUpdate( prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any ) {
		if ( prevProps.isRefreshing !== this.props.isRefreshing ) {
			if ( this.props.isRefreshing )
				showLoading();
			else hideLoading();
		}
	}

	render() {
		const { children, loader, ...props } = this.props;
		const Loader = loader!;
		return <ManagementContractContext.Provider
			value={ {
				...props,
				terminateManagementContract: this.terminateManagementContract,
				submitManagementContract: this.submitManagementContract,
			} }>
			<Loader>
				{ children }
			</Loader>
		</ManagementContractContext.Provider>;
	}

	private getTempContractId() {
		const { tempContractId } = this.state;
		showLoading();
		if ( tempContractId == null ) {
			return EjariApi.getTemporaryContractId().then( resp => {
				this.setState( { ...this.state, tempContractId: resp.TempContractId + "" } );
				hideLoading();
				return resp.TempContractId + "";
			} ).catch( e => {
				showErrorMessage( e.toString() );
				hideLoading();
				return Promise.reject();
			} );
		}
		return Promise.resolve( this.state.tempContractId! + "" );
	}

	private uploadDocs( reqDocs: TenantDocumentLookup[], managementContractId: number ): Promise<any> {
		let uploadTasks: Promise<any>[] = [];
		reqDocs.forEach( reqDoc => {
			reqDoc.Docs?.forEach( doc => {
				if ( doc.LocalData != null ) {
					const docId = doc.DocumentID;
					if ( !isEmptyStringOrNull( doc.DocumentURL ) ) {
						uploadTasks.push( Promise.resolve( doc ) );
					} else if ( doc.LocalData ) {
						const extData = doc.LocalData.split( ";" )[ 0 ];
						const type = extData.includes( "image" ) ? "jpg" : "pdf";
						let encoded = doc.LocalData.replace( /^data:(.*;base64,)?/, "" );
						if ( ( encoded.length % 4 ) > 0 ) {
							encoded += "=".repeat( 4 - ( encoded.length % 4 ) );
						}
						uploadTasks.push( EjariApi.uploadTenancyContractDocument( encoded, type, docId!, managementContractId, doc.FileName! )
							.then( resp => {
								doc.DocumentURL = resp.DocumentUrl;
								const d: any = doc;
								d.DocumentPageId = ( resp as any ).DocumentPageId;
								return Promise.resolve( d );
							} ).catch( e => {
								return Promise.reject( e.toString() );
							} )
						);
					}
				}
			} );
		} );
		return Promise.all( uploadTasks );
	}

	terminateManagementContract = ( ctx: IManagementContractContext, request: MCTerminateRequest, documents: TenantDocumentLookup[] ) => {
		showLoading();
		return this.uploadDocs( documents, ctx.id! ).then( () => {
			return EjariApi.terminateManagementContract( request ).then( resp => {
				hideLoading();
				this.props.onContractActionFinished( ctx );
				return Promise.resolve( resp );
			} ).catch( e => {
				hideLoading();
				return Promise.reject( e.toString() );
			} );
		} )
		.catch( e => {
			hideLoading();
			showErrorMessage( Strings.ask_malik_fail );
			return Promise.reject( e.toString() );
		} );
	};

	submitManagementContract = ( ctx: IManagementContractContext ) => {
		const action = ctx.action!;
		let submitRequest: Partial<MCContractCreateRequest> = {
			ActionType: this.getActionType( action ),
			ActiveUserID: getEjariActiveUserId(),
			ActiveUserType: getEjariUserType(),
			Responsibilities: this.normalizeResponsibility( ctx.responsibilities! ),
			AdditionalTerms: ctx.additionalTerms,
			...ctx.contractDetail,
			AgreementCompany: ctx.contractDetail?.AgreementCompany?.Id,
			ManagementContractActivityId: ctx.contractDetail?.ManagementContractActivityId?.Id,
			ManagementContractTypeId: ctx.contractDetail?.ManagementContractTypeId?.Id,
			Payments: ctx.payments ?? [],
			EjariPropertyId: ctx.properties!.map( p => p.EjariPropertyId + "" as any ),
		};

		delete submitRequest.contractValType;

		if ( action === TenancyContactAction.RENEW_CONTRACT ) {
			submitRequest.ManagementContractId = ctx.managementContractDetails?.ManagementContractId;
			submitRequest.EjariPropertyId = ctx.managementContractDetails?.AssociatedDetailedProperties.map( p => p.PropertyEpropId );
		}

		const api = action === TenancyContactAction.RENEW_CONTRACT ? EjariApi.renewManagementContract : EjariApi.createManagementContract;

		showLoading();
		EjariApi.validateManagementContract( submitRequest as MCContractCreateRequest ).then( () => {
			hideLoading();
			this.getTempContractId().then( tempContractId => {
				showLoading();
				this.uploadDocs( ctx.documents!, Number( tempContractId ) )
				.then( response => {
					submitRequest.DocumentTemporaryID = response?.[ 0 ].DocumentPageId;
					this.setState( { ...this.state, documentTempId: response?.[ 0 ].DocumentPageId } );
					api( submitRequest as MCContractCreateRequest )
					.then( () => {
						hideLoading();
						this.props.onContractActionFinished( ctx );
						trackEvent( "ManagementContract", action === TenancyContactAction.RENEW_CONTRACT ? "RenewSubmitSuccess" : "CreateSubmitSuccess" );
					} )
					.catch( e => {
						hideLoading();
						showErrorMessage( e );
					} );
				} )
				.catch( e => {
					hideLoading();
					showErrorMessage( e );
				} );
			} );
		} ).catch( ex => {
			hideLoading();
			showErrorMessage( ex );
		} );

		return Promise.resolve();
	};

	private getActionType( action: TenancyContactAction ) {
		if ( action === TenancyContactAction.NEW_CONTRACT ) return 9;
		if ( action === TenancyContactAction.RENEW_CONTRACT ) return 10;
		return 9;
	}

	normalizeResponsibility( terms: OptionEJLookup[] ): Responsibilities[] {
		let s: Responsibilities[] = [];
		terms.forEach( value => {
			s.push( {
				DescriptionAr: value.NameAr,
				DescriptionEn: value.NameEn,
				ResponsibilityID: value.Identity,
				IsCompany: !value.isOwner,
			} );
		} );
		return s;
	}
}

const mapStateToProps = ( state: IStore, _ownProps: Partial<Props> & { children?: any } ): Props => {
	return {
		...state.ManagementContractData,
	} as Props;
};

export default connect( mapStateToProps )( ManagementContractContextWrapper );

export function updateManagementContractContext( data: Partial<ManagementContractDataState> ) {
	dispatchX( {
		type: ManagementContractDataAction.MANAGEMENT_CONTRACT_UPDATE_STATE,
		payload: {
			...data
		}
	} );
}

export function resetManagementContractContext() {
	dispatchX( {
		type: ManagementContractDataAction.MANAGEMENT_CONTRACT_CLEAR_DATA,
	} );
}
